import { initialize } from "launchdarkly-js-client-sdk";
import type { LDClient, LDFlagSet } from "launchdarkly-js-client-sdk";

import {
  LocalNativeBuilderFlags,
  shouldSendLaunchDarklyEvents,
} from "@superblocksteam/shared";

import { bootstrapFlags } from "core/utils/bootstrapFlags";
import { isCloudPrem } from "core/utils/deployment";
import envs from "env";
import { isProduction } from "utils/env";
import logger from "utils/logger";

import { Flag } from "./models/Flags";
import type { Flags } from "./models/Flags";

interface FeatureFlagManager {
  getKeysForOrg: (typeof FeatureFlagManagerLaunchDarkly)["prototype"]["getKeysForOrg"];
}

// Force all non-production envs to enable the code formatting FF.
// Remove conditional flow with EG-18006
function withNonProductionFlags(flags: LDFlagSet): Flags {
  if (isProduction()) {
    return flags;
  }

  const localFlags = LocalNativeBuilderFlags.forBrowser(
    envs.get("SUPERBLOCKS_UI_LOCAL_BUILDER_RUNTIME") === "true",
    window.location.hostname,
  );
  return {
    ...localFlags.overlay(flags),
    [Flag.CODE_FORMATTING]: true,
    [Flag.HOMEPAGE_TAGGING]: true,
  };
}

class FeatureFlagManagerLaunchDarkly {
  private clientKey;
  private client?: LDClient;
  // Every flag fetch on a misconfigured server would repeat it, so log it once per page load.
  private missingBootstrapLogged = false;

  constructor(clientKey: string) {
    this.clientKey = clientKey;
  }

  getKeysForOrg = async (
    orgId?: string,
    organizationBillingPlan?: string,
    created?: string,
    bootstrap?: LDFlagSet,
    organizationName?: string,
  ): Promise<Flags> => {
    // Cloud-prem uses only the server bootstrap. The LaunchDarkly client is never started, so
    // nothing streams, polls or re-identifies, even when the bootstrap is missing.
    if (isCloudPrem()) {
      const flags = bootstrapFlags(bootstrap);
      if (!flags && !this.missingBootstrapLogged) {
        this.missingBootstrapLogged = true;
        logger.error(
          "Cloud-prem server sent no valid feature flag bootstrap; using default flag values",
          { superblocks_org_id: orgId },
        );
      }
      return withNonProductionFlags(flags ?? {});
    }
    // https://docs.launchdarkly.com/sdk/client-side/javascript/migration-2-to-3#understanding-differences-between-users-and-contexts
    const user = {
      key: orgId,
      kind: "user",
      created,
      plan: organizationBillingPlan,
      name: organizationName,
    };
    if (this.client === undefined) {
      // Bootstrap from server
      // https://docs.launchdarkly.com/sdk/features/bootstrapping
      this.client = initialize(this.clientKey, user, {
        // Any object makes the SDK ready immediately; without one it fetches flags over the network.
        bootstrap: bootstrap ?? {},
        evaluationReasons: true,
        sendEvents: shouldSendLaunchDarklyEvents(
          envs.get("SUPERBLOCKS_UI_LAUNCH_DARKLY_SEND_EVENTS_ENABLED"),
        ),
      });
      await this.client.waitForInitialization(5);
    } else if (this.client.getContext()?.key !== orgId) {
      await this.client.identify(user);
    }
    return withNonProductionFlags(this.client.allFlags());
  };
}

const flagClient: FeatureFlagManager = new FeatureFlagManagerLaunchDarkly(
  envs.get("SUPERBLOCKS_UI_LAUNCH_DARKLY_CLIENT_KEY"),
);
export default flagClient;
